/**
 * About Us Page
 * Provides information about PayWise365 and builds trust with visitors
 */
import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { useCustomTranslation } from '../hooks';
import { Box, Container, Typography, Button, Card, CardContent, Stack, Divider, Alert } from '@mui/material';
import { trackPageView } from '../utils/analytics';
import SEO from '../components/common/SEO';

export default function About() {
  const { t, i18n } = useCustomTranslation();
  const isEnglish = i18n.language === 'en';

  useEffect(() => {
    trackPageView('/about', 'About');
    document.title = 'About PayWise365 | PayWise365';
  }, []);

  const seoTitle = isEnglish
    ? 'About PayWise365 - Free Hong Kong Mortgage Calculator Suite'
    : '關於 PayWise365 - 香港免費按揭計算機套裝';

  const seoDescription = isEnglish
    ? 'Learn about PayWise365, Hong Kong\'s trusted free mortgage and financial calculator platform. Our mission is to empower homeowners and investors with accurate, easy-to-use financial planning tools.'
    : '了解 PayWise365，香港信賴的免費按揭及理財計算機平台。我們的使命是以準確、易用的理財規劃工具賜能業主和投資者。';

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <SEO
        title={seoTitle}
        description={seoDescription}
        canonicalUrl="https://www.paywise365.com/about"
        ogType="website"
      />
      {/* Page Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: { xs: 6, md: 8 },
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h2" sx={{ mb: 2, fontWeight: 700 }}>
            About PayWise365
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.95, fontWeight: 400 }}>
            Your trusted partner in mortgage planning
          </Typography>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Stack spacing={4}>
          {/* Main Content */}
          <Stack spacing={4}>
            {/* Mission */}
            <Box>
              <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
                Our Mission
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                PayWise365 empowers Hong Kong residents with free, accurate financial calculators
                to make informed decisions about loans, mortgages, and debt repayment.
              </Typography>
            </Box>

            <Divider />

            {/* What We Offer */}
            <Box>
              <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
                What We Offer
              </Typography>
              <Stack spacing={3}>
                <Box>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    Loan Calculators
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Calculate monthly payments, total interest costs, and view complete amortization schedules.
                    Supports monthly and fortnightly payment options.
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    Partial Repayment Calculator
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    See how extra payments can save thousands in interest. Compare reducing payment amount
                    or loan period strategies.
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    Multi-Language Support
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Available in English, Traditional Chinese, and Cantonese to serve Hong Kong's diverse community.
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <Divider />

            {/* How It Works */}
            <Box>
              <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
                How It Works
              </Typography>
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  <strong>1. Enter Details:</strong> Input loan amount, interest rate, and term
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>2. Choose Frequency:</strong> Select monthly or fortnightly payments
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>3. Get Results:</strong> View instant calculations and detailed breakdowns
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>4. Make Decisions:</strong> Use insights to compare offers and plan repayments
                </Typography>
              </Stack>
            </Box>

            <Divider />

            {/* Disclaimers */}
            <Box>
              <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
                Important Information
              </Typography>
              <Stack spacing={2}>
                <Alert severity="warning" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Not Financial Advice
                  </Typography>
                  <Typography variant="body2">
                    PayWise365 is an educational tool for informational purposes only.
                    Always consult with a licensed financial advisor before making significant financial decisions.
                  </Typography>
                </Alert>
                <Alert severity="info" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Calculation Limitations
                  </Typography>
                  <Typography variant="body2">
                    Our calculators provide estimates based on standard formulas. They may not account for
                    application fees, early repayment penalties, or variable rate changes.
                    Always verify terms with your lender.
                  </Typography>
                </Alert>
              </Stack>
            </Box>
          </Stack>

          {/* Why Choose PayWise365 - From Home Page */}
          <Box>
            <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, textAlign: 'center' }}>
              {t('home.features.title')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 5, textAlign: 'center' }}>
              {t('home.features.subtitle')}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 4 }}>
              <Card sx={{ textAlign: 'center', transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 } }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ fontSize: '3rem', mb: 2 }}>🎯</Box>
                  <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>
                    {t('home.features.accurate.title')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    {t('home.features.accurate.description')}
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ textAlign: 'center', transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 } }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ fontSize: '3rem', mb: 2 }}>🔒</Box>
                  <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>
                    {t('home.features.private.title')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    {t('home.features.private.description')}
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ textAlign: 'center', transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 } }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ fontSize: '3rem', mb: 2 }}>🌐</Box>
                  <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>
                    {t('home.features.multilingual.title')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    {t('home.features.multilingual.description')}
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ textAlign: 'center', transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 } }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ fontSize: '3rem', mb: 2 }}>📱</Box>
                  <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>
                    {t('home.features.mobile.title')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    {t('home.features.mobile.description')}
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ textAlign: 'center', transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 } }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ fontSize: '3rem', mb: 2 }}>📊</Box>
                  <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>
                    {t('home.features.visual.title')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    {t('home.features.visual.description')}
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ textAlign: 'center', transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 } }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ fontSize: '3rem', mb: 2 }}>💰</Box>
                  <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>
                    {t('home.features.free.title')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    {t('home.features.free.description')}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Box>

          {/* CTA */}
          <Card sx={{ bgcolor: 'primary.light', color: 'white' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
                Ready to Calculate?
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
                Start using our free calculators now
              </Typography>
              <Button
                component={Link}
                to="/"
                variant="contained"
                size="large"
                sx={{
                  bgcolor: 'white',
                  color: 'primary.main',
                  px: 4,
                  '&:hover': {
                    bgcolor: 'grey.100',
                  },
                }}
              >
                Go to Calculators
              </Button>
            </CardContent>
          </Card>
        </Stack>
      </Container>
    </Box>
  );
}
